import React, { useEffect } from "react";
import { useState, Fragment } from "react";
import { Card, Row, Col, Collapse, Tooltip, OverlayTrigger } from "react-bootstrap";
import { Address } from "../../Physician/Models/Address";
import { AddressVerificationResult } from "../../Physician/Models/AddressVerificationResult";
import { AddressType } from "../Enums/AddressType";
import { deepCopy } from "../Utility/DeepCopy";
import { CountryLookupItem, StateWithCountryLookupItem } from "../Models/StateWithCountryLookupItem";
import AddressForm from "./AddressForm";
import { CountryId } from "../Enums/CountryId";

interface IProps {
    isNewPhysician: boolean;
    fullName: string;
    addresses: Array<Address>;
    emailAddress: string;
    states: Array<StateWithCountryLookupItem>;
    countries: Array<CountryLookupItem>;
    isSectionOpen: boolean;
    runValidation: boolean;
    onSave: (addresses: Array<Address>) => void;
    onUpdate?: () => void;
    onOpen?: () => void;
    disabled?: boolean;
    isLoading?: boolean;
}
export function ApplicantInformation(props: IProps) {
    interface IData {
        openCollapse: boolean;
        addressVerificationResult: Array<AddressVerificationResult>;
        contactAddress: Address;
    }

    const createNewContactAddress = () => {
        var newContactAddress = new Address();
        newContactAddress.countryId = CountryId.Usa;
        newContactAddress.types.push(AddressType.Contact);
        newContactAddress.isContact = true;
        return newContactAddress;
    }

    const [data, setData] = useState<IData>({
        openCollapse: props.isSectionOpen,
        addressVerificationResult: null,
        contactAddress: props.addresses?.find(_ => _.isContact) ?? createNewContactAddress()
    });

    useEffect(() => {
        setData(prevData => ({...prevData, openCollapse: props.isSectionOpen}));
    }, [props.isSectionOpen]);

    const buildAddressList = (contactAddress: Address) => {
        // must have 1 of each type to save: contact, shipping, and public
        let addresses = new Array<Address>();
        addresses.push(contactAddress);

        if(contactAddress.addressId == 0) {
            //Add public and shipping address types to contact address
            contactAddress.types.push(AddressType.Public);
            contactAddress.types.push(AddressType.Shipping);
            return addresses;
        }
        else {
            var publicationAndShippingAddress = props.addresses?.find(_ =>
                _.types.includes(AddressType.Public)
                && _.types.includes(AddressType.Shipping)
                && !_.isContact);
            if (publicationAndShippingAddress) {
                addresses.push(publicationAndShippingAddress);
                return addresses;
            }

            contactAddress.types = [AddressType.Contact, AddressType.Shipping, AddressType.Public];

            var publicationAddress = props.addresses?.find(_ => _.types.includes(AddressType.Public) && !_.isContact);
            if (publicationAddress) {
                addresses.push(publicationAddress);
                contactAddress.types.splice(contactAddress.types.indexOf(AddressType.Public), 1);
            }

            var shippingAddress = props.addresses?.find(_ => _.types.includes(AddressType.Shipping) && !_.isContact);
            if (shippingAddress) {
                addresses.push(shippingAddress);
                contactAddress.types.splice(contactAddress.types.indexOf(AddressType.Shipping), 1);
            }

            return addresses;
        }
    }

    const saveContactAddress = (contactAddress: Address) => {
        let addresses = buildAddressList(contactAddress);
        props.onSave(addresses);
    }

    const setOpenCollapse = () => {
        let updatedData = deepCopy(data);
        updatedData.openCollapse = !updatedData.openCollapse;
        if (updatedData.openCollapse && props.onOpen) props.onOpen();
        setData(updatedData);
    }

    return (
        <Card className="mb-4" border="pp-secondary-grey">
            <Card.Body>
                {props.isNewPhysician ?
                    <Card.Title className="mb-0  font-weight-bolder">
                        <span className="text-pp-primary">Contact Address</span>
                    </Card.Title> :
                    <Card.Title className="mb-0 font-weight-bolder" onClick={() => {if (!props.disabled) setOpenCollapse()}}>
                        <span className={`text-pp-primary ${props.disabled && 'pp-disabled'}`}><i className="text-pp-primary las la-angle-down"></i> Applicant</span>
                    </Card.Title>}
                <Collapse in={data.openCollapse}>
                    <Row className="mt-2">
                        <Col xs={12} md={12} lg={12} xl={12}>
                            {!props.isNewPhysician &&
                                <Fragment>
                                    <Row>
                                        <Col>
                                            <Card.Text className="font-weight-bolder mb-3 text-uppercase pp-font-size-smaller">Contact
                                                <OverlayTrigger
                                                    placement={"right"}
                                                    overlay={<Tooltip id="contactInfo" bsPrefix="pp-tooltip">
                                                        Contact Address is used for the delivery of any mailed items.
                                                    </Tooltip>}>
                                                    <i className="las la-info-circle ml-1 la-lg"></i>
                                                </OverlayTrigger>
                                            </Card.Text>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col>
                                            <Card className="h-100 d-flex flex-column justify-content-end border-0">
                                                <Card.Text className="font-weight-bolder mb-1 pp-font-size-smaller">
                                                    Applicant Name
                                                </Card.Text>
                                                <Card.Text className="pp-font-size-smaller">
                                                    {props.fullName}
                                                </Card.Text>
                                            </Card>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col>
                                            <Card className="h-100 mt-3 mb-3 d-flex flex-column justify-content-end border-0">
                                                <Card.Text className="font-weight-bolder mb-1 pp-font-size-smaller">
                                                    Email
                                                </Card.Text>
                                                <Card.Text className="pp-font-size-smaller">
                                                    {props.emailAddress ? props.emailAddress : "placeholder@email.com"}
                                                </Card.Text>
                                            </Card>
                                        </Col>
                                    </Row>
                                </Fragment>}
                            <AddressForm
                                runValidation={props.runValidation}
                                address={data.contactAddress}
                                states={props.states}
                                countries={props.countries}
                                onSubmit={saveContactAddress}
                                onUpdate={props.onUpdate}
                                showSubmit={!props.isNewPhysician}
                                isLoading={props.isLoading}
                            />
                        </Col>
                    </Row>
                </Collapse>
            </Card.Body>
        </Card>
    );
}

export default ApplicantInformation;
